<script lang="ts">
import FeedbackIcon from '@/assets/icons/feedback.svg?component'
import CharacterCount from '@/components/utils/CharacterCount.vue'
import CloseButton from '@/components/utils/CloseButton.vue'
import CodexButton from '@/components/utils/CodexButton.vue'
import CodexDropdown from '@/components/utils/CodexDropdown.vue'
import Modal from '@/components/utils/Modal.vue'
import { useFeedbackContext } from '@/composables/useFeedbackContext'
import useModal from '@/composables/useModal'
import { KEYCODES } from '@/lib/Constants'
import {
  clampWithNotice,
  DraftErrors,
  DraftField,
  DropNotice,
  firstInvalidField,
  hasUnsavedWork,
  MAX_DETAILS,
  MAX_SUMMARY,
  newDraft,
  nextDropNotice,
  REQUEST_TYPES,
  RequestType,
  validateDraft
} from '@/lib/feedbackDraft'
import { buildBody, buildSubject, mailtoHref, SUPPORT_EMAIL } from '@/lib/feedbackMail'
import { computed, defineComponent, nextTick, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

const modalId = 'feedbackWidget'

/*
  One name for the trigger and for the dialog it opens. Held here rather than written twice,
  because a user who clicks a control called one thing must land in a dialog called the same.
*/
const WIDGET_NAME = 'Feedback & support'

/* One dialog, three views. A second Modal would mean a second focus trap. */
type View = 'form' | 'confirmDiscard' | 'composed'

type ComposedMessage = {
  subject: string
  body: string
  href: string
}

export default defineComponent({
  name: 'FeedbackWidget',
  components: {
    CharacterCount,
    CloseButton,
    CodexButton,
    CodexDropdown,
    FeedbackIcon,
    Modal
  },
  setup() {
    const { closeModal, isOpen, openModal } = useModal(modalId)
    const { captureContext } = useFeedbackContext()
    const route = useRoute()

    const draft = ref(newDraft())
    const view = ref<View>('form')
    const errors = ref<DraftErrors>({})
    const dropNotice = ref<DropNotice>(null)
    const composed = ref<ComposedMessage | null>(null)
    const copyState = ref<'idle' | 'copied' | 'failed'>('idle')

    const summaryInput = ref<HTMLInputElement>()
    const detailsInput = ref<HTMLTextAreaElement>()
    const keepWritingButton = ref<{ $el?: HTMLElement }>()
    const composedHeading = ref<HTMLElement>()

    const isDirty = computed(() => hasUnsavedWork(draft.value))

    /*
      While this is true, Modal must not close on Escape or on a backdrop click, because
      doing so would discard what the user wrote. closeDisabled is how Modal exposes that,
      and the header-actions slot below replaces the close control it hides in return.
    */
    const dismissalGuarded = computed(() => {
      if (view.value === 'confirmDiscard') {
        return true
      }

      return view.value === 'form' && isDirty.value
    })

    const limitFor = (field: DraftField) => (field === 'summary' ? MAX_SUMMARY : MAX_DETAILS)

    const fieldElement = (field: DraftField) =>
      field === 'summary' ? summaryInput.value : detailsInput.value

    const dropNoticeText = computed(() => {
      if (!dropNotice.value) {
        return ''
      }

      const { field, dropped } = dropNotice.value
      const name = field === 'summary' ? 'summary' : 'details'
      const characters = dropped === 1 ? 'character was' : 'characters were'

      return `${dropped} ${characters} removed. The ${name} field accepts ${limitFor(field)} characters.`
    })

    function focusField(field: DraftField) {
      fieldElement(field)?.focus()
    }

    function resetDraft() {
      draft.value = newDraft()
      view.value = 'form'
      errors.value = {}
      dropNotice.value = null
      composed.value = null
      copyState.value = 'idle'
    }

    /*
      The fields carry no maxlength attribute on purpose. The browser would truncate an
      over-long paste silently, and the user has to be told what was removed, so the clamp
      happens here where a notice can be raised with it.
    */
    async function onFieldInput(field: DraftField, rawValue: string) {
      const max = limitFor(field)
      const clamped = clampWithNotice(rawValue, max)

      draft.value[field] = clamped.value
      dropNotice.value = nextDropNotice(dropNotice.value, field, clamped, max)

      if (errors.value[field] && clamped.value.trim()) {
        errors.value = { ...errors.value, [field]: undefined }
      }

      /*
        A clamp that lands on the value already held is not a reactive change, so Vue would
        leave the extra characters sitting in the DOM. Push the clamped value back by hand.
      */
      await nextTick()
      const element = fieldElement(field)
      if (element && element.value !== clamped.value) {
        element.value = clamped.value
      }
    }

    function onRequestTypeInput(value: RequestType) {
      draft.value.requestType = value
    }

    async function showConfirmDiscard() {
      view.value = 'confirmDiscard'
      await nextTick()
      keepWritingButton.value?.$el?.focus()
    }

    async function returnToForm() {
      view.value = 'form'
      await nextTick()
      focusField('summary')
    }

    /*
      Every dismissal path funnels through here: the header close control, Cancel, Escape,
      and a backdrop click. One decision point means the three views cannot disagree about
      what a dismissal does.
    */
    function requestDismissal() {
      if (view.value === 'confirmDiscard') {
        returnToForm()
        return
      }

      if (view.value === 'form' && isDirty.value) {
        showConfirmDiscard()
        return
      }

      closeModal()
    }

    function discardDraft() {
      closeModal()
    }

    async function prepareEmail() {
      const found = validateDraft(draft.value)
      errors.value = found

      const invalid = firstInvalidField(found)
      if (invalid) {
        focusField(invalid)
        return
      }

      const context = captureContext()
      const message = {
        subject: buildSubject(draft.value),
        body: buildBody(draft.value, context),
        href: mailtoHref(draft.value, context)
      }

      composed.value = message
      copyState.value = 'idle'
      view.value = 'composed'

      openMailDraft(message.href)

      /*
        The form is removed by v-if, and the control the user just operated goes with it, so
        focus would fall back to <body> and the reader would be told nothing. Move it to the
        heading of the view that replaced the form, which is what the other two view changes
        do. The heading is first in that view, so reading forward from it covers the whole
        message rather than starting the user at the actions below it.
      */
      await nextTick()
      composedHeading.value?.focus()
    }

    /*
      A browser with no mail handler ignores this without any signal, which is exactly why
      the composed view stays open behind it holding the whole message.
    */
    function openMailDraft(href: string) {
      const link = document.createElement('a')
      link.href = href
      link.rel = 'noopener'
      link.click()
    }

    const plainTextMessage = computed(() => {
      if (!composed.value) {
        return ''
      }

      return [
        `To: ${SUPPORT_EMAIL}`,
        `Subject: ${composed.value.subject}`,
        '',
        composed.value.body
      ].join('\r\n')
    })

    async function copyMessage() {
      try {
        await navigator.clipboard.writeText(plainTextMessage.value)
        copyState.value = 'copied'
      } catch {
        // A denied permission or an insecure context. The text stays selectable either way.
        copyState.value = 'failed'
      }
    }

    /* Modal's own Escape handler is inert while closeDisabled is set, so take that case. */
    function onKeydown(event: KeyboardEvent) {
      if (event.code !== KEYCODES.ESC || !dismissalGuarded.value) {
        return
      }

      event.preventDefault()
      requestDismissal()
    }

    /*
      Modal binds its backdrop click to its own handler, which is also inert while
      closeDisabled is set. The backdrop is the dialog's parent element, so it can be
      reached without changing Modal, which 20+ other components depend on.
    */
    const backdropElement = () =>
      document.querySelector<HTMLElement>(`[data-testid="modal-${modalId}"]`)?.parentElement

    function onBackdropClick(event: MouseEvent) {
      if (event.target !== event.currentTarget || !dismissalGuarded.value) {
        return
      }

      requestDismissal()
    }

    function attachDismissalListeners() {
      window.addEventListener('keydown', onKeydown)
      backdropElement()?.addEventListener('click', onBackdropClick)
    }

    function detachDismissalListeners() {
      window.removeEventListener('keydown', onKeydown)
      backdropElement()?.removeEventListener('click', onBackdropClick)
    }

    watch(isOpen, async (open) => {
      if (open) {
        await nextTick()
        // Modal traps Tab but never moves focus into the dialog, so place it on the first field.
        focusField('summary')
        attachDismissalListeners()
        return
      }

      detachDismissalListeners()
      resetDraft()
    })

    /*
      The widget is mounted once in App.vue, so it outlives the route. Without this it would
      carry the address of a page the user has already left.
    */
    watch(
      () => route?.fullPath,
      () => {
        if (isOpen.value) {
          closeModal()
        }
      }
    )

    return {
      canSubmit: computed(() => Object.keys(validateDraft(draft.value)).length === 0),
      closeModal,
      composed,
      composedHeading,
      copyMessage,
      copyState,
      details: computed(() => draft.value.details),
      DETAILS_LIMIT: MAX_DETAILS,
      discardDraft,
      dismissalGuarded,
      dropNoticeText,
      errors,
      isOpen,
      modalId,
      keepWritingButton,
      onFieldInput,
      onRequestTypeInput,
      openModal,
      prepareEmail,
      // Copied because REQUEST_TYPES is readonly, and CodexDropdown asks for a mutable array.
      REQUEST_TYPES: [...REQUEST_TYPES],
      requestDismissal,
      requestType: computed(() => draft.value.requestType),
      subject: computed(() => draft.value.summary),
      SUBJECT_LIMIT: MAX_SUMMARY,
      SUPPORT_EMAIL,
      summaryInput,
      detailsInput,
      view,
      WIDGET_NAME
    }
  }
})
</script>

<template>
  <!--
    No z-index: the trigger is positioned, so it already paints above the app's
    in-flow chrome. The open dialog's backdrop sits at z-index: 1040, so the
    trigger stays below it regardless of what value this element carries.
  -->
  <div :class="$style.trigger">
    <CodexButton
      :class="$style.triggerButton"
      data-testid="feedbackWidget__triggerButton"
      @click-button="openModal()"
    >
      <FeedbackIcon aria-hidden="true" />
      <span :class="$style.triggerLabel">{{ WIDGET_NAME }}</span>
    </CodexButton>
  </div>

  <!--
    The title is bound, not fixed, and it is dropped while the dialog is shut. Modal hardcodes
    id="title" on the heading this prop renders, teleports itself to <body>, and hides itself
    with CSS alone, so a mounted Modal is always in the document. This widget is mounted once
    in App.vue and never unmounted, so a fixed title would leave a permanent id="title"
    reading "Feedback & support" in the page, and every other dialog — each of which is
    labelled by aria-labelledby="title" — would take its name from this widget instead of
    from its own heading. Dropping the title while shut restores the rule the app already
    depended on: only one dialog holds that id at a time.

    A v-if on <Modal> would look like the simpler fix and is not. Modal's watch on isOpen
    carries no immediate flag, so a Modal that mounts already open never locks the page
    scroll, never installs its Tab trap and never emits open; its dismissal listeners here
    are attached by element lookup as well, and the element would be gone before they could
    be removed. Passing the title through Modal's #title slot instead would also work, but
    Vue does not apply a component's scoped styles to content slotted into it, so the heading
    would have to carry a copy of Modal's header styling and would drift away from it.
  -->
  <Modal
    :modal-id="modalId"
    :title="isOpen ? WIDGET_NAME : undefined"
    width="90vw"
    max-width="25rem"
    max-height="85vh"
    :close-disabled="dismissalGuarded"
    :click-outside-to-close="!dismissalGuarded"
  >
    <!--
      Modal hides its own close control while closeDisabled is set. This slot puts it back,
      routed through the dismissal guard, so a user who has typed something never loses the
      one control they expect to find.
    -->
    <template #header-actions>
      <CloseButton
        aria-label="Close feedback and support."
        data-testid="feedbackWidget__closeButton"
        @click="requestDismissal()"
      />
    </template>

    <!--
      v-if, not v-show: getAllFocusableElements filters on an element's own inline display,
      so a v-show'd form would keep its inputs in Modal's Tab trap while they are invisible.
      The draft lives in component state, so nothing is lost when the markup goes away.
    -->
    <form
      v-if="view === 'form'"
      class="codexForm"
      :class="$style.form"
      data-testid="feedbackWidget__form"
      @submit.prevent="prepareEmail"
    >
      <p :class="$style.intro">
        Send a question, a problem report, or a suggestion to the IMS support team.
      </p>

      <CodexDropdown
        :class="$style.requestType"
        label-text="Request type"
        name="feedbackRequestType"
        data-testid="feedbackWidget__requestTypeInput"
        :options="REQUEST_TYPES"
        :model-value="requestType"
        :searchable="false"
        @input="onRequestTypeInput"
      />

      <label for="feedbackSummary">Summary <span>required</span></label>
      <input
        id="feedbackSummary"
        ref="summaryInput"
        type="text"
        name="feedbackSummary"
        data-testid="feedbackWidget__summaryInput"
        :value="subject"
        :aria-invalid="errors.summary ? 'true' : 'false'"
        :aria-describedby="errors.summary ? 'feedbackSummaryError' : undefined"
        @input="onFieldInput('summary', ($event.target as HTMLInputElement).value)"
      />
      <p
        v-if="errors.summary"
        id="feedbackSummaryError"
        class="errorMessage"
        role="alert"
        data-testid="feedbackWidget__summaryError"
      >
        {{ errors.summary }}
      </p>
      <!--
        aria-hidden: after a clamp the count alone reads as though the whole text were
        accepted. The announcement below the fields carries the loss instead.
      -->
      <p :class="$style.counter" aria-hidden="true">
        <!--
          count-basis: onFieldInput clamps in code points, so a count in code units would
          contradict the field beside it. Ten emoji left in the summary would read as twenty
          characters used and the counter would reach zero, and then go red and negative,
          while the field went on accepting ten more.
        -->
        <CharacterCount :character-limit="SUBJECT_LIMIT" :text="subject" count-basis="codePoints" />
        characters left
      </p>

      <label for="feedbackDetails">Details <span>required</span></label>
      <textarea
        id="feedbackDetails"
        ref="detailsInput"
        name="feedbackDetails"
        rows="5"
        data-testid="feedbackWidget__detailsInput"
        :value="details"
        :aria-invalid="errors.details ? 'true' : 'false'"
        :aria-describedby="errors.details ? 'feedbackDetailsError' : undefined"
        @input="onFieldInput('details', ($event.target as HTMLTextAreaElement).value)"
      />
      <p
        v-if="errors.details"
        id="feedbackDetailsError"
        class="errorMessage"
        role="alert"
        data-testid="feedbackWidget__detailsError"
      >
        {{ errors.details }}
      </p>
      <p :class="$style.counter" aria-hidden="true">
        <CharacterCount :character-limit="DETAILS_LIMIT" :text="details" count-basis="codePoints" />
        characters left
      </p>

      <p
        v-if="dropNoticeText"
        :class="$style.dropNotice"
        role="alert"
        data-testid="feedbackWidget__dropNotice"
      >
        {{ dropNoticeText }}
      </p>

      <p :class="$style.disclosure" data-testid="feedbackWidget__disclosure">
        The draft also carries the page you are on, its web address, the current time, your browser
        version, and your site name. You can read and change all of it before you send the message.
      </p>
    </form>

    <div
      v-if="view === 'confirmDiscard'"
      :class="$style.confirm"
      data-testid="feedbackWidget__confirmDiscard"
    >
      <p :class="$style.confirmTitle" role="alert">Discard this message?</p>
      <p :class="$style.intro">
        You have written something that has not been sent yet. Discarding it cannot be undone.
      </p>
    </div>

    <div v-if="view === 'composed'" :class="$style.composed" data-testid="feedbackWidget__composed">
      <!--
        tabindex, and no role="status": focus is moved here when the view opens, so a reader
        announces the heading on arrival. A live region on the same words would say them a
        second time. The confirm view keeps its role="alert" because focus goes to a button
        there, not to the heading.
      -->
      <p
        ref="composedHeading"
        :class="$style.confirmTitle"
        tabindex="-1"
        data-testid="feedbackWidget__composedHeading"
      >
        Your message is ready to send.
      </p>
      <p :class="$style.intro">
        A draft should have opened in your mail application. If it did not, copy the message below
        and send it yourself.
      </p>

      <dl :class="$style.summaryList">
        <dt>To</dt>
        <dd data-testid="feedbackWidget__composedRecipient">{{ SUPPORT_EMAIL }}</dd>
        <dt>Subject</dt>
        <dd data-testid="feedbackWidget__composedSubject">{{ composed?.subject }}</dd>
      </dl>

      <!-- tabindex: the block scrolls, so it needs keyboard access of its own. -->
      <pre
        :class="$style.message"
        tabindex="0"
        aria-label="The message that will be sent"
        data-testid="feedbackWidget__composedBody"
        >{{ composed?.body }}</pre
      >

      <p
        v-if="copyState === 'copied'"
        :class="$style.copyStatus"
        role="status"
        data-testid="feedbackWidget__copyStatus"
      >
        The message is on your clipboard.
      </p>
      <p
        v-if="copyState === 'failed'"
        :class="$style.dropNotice"
        role="alert"
        data-testid="feedbackWidget__copyError"
      >
        Your browser would not let us reach the clipboard. Select the message above and copy it by
        hand.
      </p>

      <p :class="$style.disclosure">
        <a :href="composed?.href" data-testid="feedbackWidget__openDraftLink"
          >Open the draft in your mail application</a
        >
      </p>
    </div>

    <template #footer>
      <template v-if="view === 'form'">
        <CodexButton
          secondary-button
          data-testid="feedbackWidget__cancelButton"
          @click-button="requestDismissal()"
        >
          Cancel
        </CodexButton>
        <CodexButton
          :disabled="!canSubmit"
          data-testid="feedbackWidget__submitButton"
          @click-button="prepareEmail"
        >
          Prepare email
        </CodexButton>
      </template>

      <!-- Keep first, and focused on entry: the destructive choice is never the default. -->
      <template v-else-if="view === 'confirmDiscard'">
        <CodexButton
          ref="keepWritingButton"
          data-testid="feedbackWidget__keepWritingButton"
          @click-button="requestDismissal()"
        >
          Keep writing
        </CodexButton>
        <CodexButton
          secondary-button
          data-testid="feedbackWidget__discardButton"
          @click-button="discardDraft()"
        >
          Discard
        </CodexButton>
      </template>

      <template v-else>
        <CodexButton
          secondary-button
          data-testid="feedbackWidget__copyButton"
          @click-button="copyMessage()"
        >
          Copy message
        </CodexButton>
        <CodexButton data-testid="feedbackWidget__doneButton" @click-button="closeModal()">
          Done
        </CodexButton>
      </template>
    </template>
  </Modal>
</template>

<style module>
.trigger {
  position: fixed;
  right: 1.25rem;
  bottom: 1.25rem;
}

/* Clear the bottom navigation, which becomes visible at this width. */
@media all and (max-width: 812px) {
  .trigger {
    bottom: 5rem;
  }
}

/* A floating control has no meaning on paper. */
@media print {
  .trigger {
    display: none;
  }
}

.triggerButton {
  gap: 0.5rem;
  border: none;
  border-radius: 2rem;
  padding: 0.625rem 1.125rem;
  box-shadow: 0 2px 10px rgba(3, 3, 61, 0.35);
}

.triggerButton svg {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
}

@media all and (max-width: 600px) {
  .triggerLabel {
    /* Keep the accessible name; drop the visible label to an icon-only button. */
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  }
}

.form {
  /*
    style.css sets no font-family on body, so every codexForm in the app inherits
    the browser default serif. Gilroy is the product face used by headings and
    buttons — pinned here so this dialog is not the odd one out. Raised for
    review: the same gap affects every other form in the app.
  */
  font-family: 'Gilroy', sans-serif;
}

.form label,
.form input,
.form textarea {
  font-family: inherit;
}

/*
  forms.css caps inputs at 25rem but leaves them content-box, so their padding and
  border push them 18px past the dropdown, which is border-box at the same 25rem.
  Border-box makes the two agree, so every field shares one right edge.
*/
.form input,
.form textarea {
  box-sizing: border-box;
}

/*
  One rhythm for every field, whatever precedes it. Margins collapse against the
  counter above, so the gap stays the same between all pairs of fields. The bottom
  margin keeps the focus outline, which is offset 2px, clear of the label text.
*/
.form label {
  margin-top: 1.25rem;
  margin-bottom: 0.35rem;
}

.form .errorMessage,
.form :global(.errorMessage) {
  min-height: 0;
  margin: 0.35rem 0 0;
}

/*
  CodexDropdown always reserves a line for its validation message, and the request type
  carries no validation rules — it has a default and cannot be empty. That line is dead
  space here, so it is dropped. The two fields that do have rules keep theirs.
*/
.requestType :global(.errorMessage) {
  display: none;
}

.intro {
  margin: 0;
  color: #636363;
}

.counter {
  margin: 0.25rem 0 0;
  text-align: right;
  font-size: 0.85rem;
  color: #666666;
}

.dropNotice {
  margin: 0.75rem 0 0;
  color: #c93b41;
  font-size: 0.85rem;
}

/*
  #666666, not the #7b7c7b that forms.css uses for the same job: measured against white,
  #7b7c7b gives 4.19:1, and 13.6px text needs 4.5:1. #666666 gives 5.74:1 and is already
  the colour of the counters below. The same shortfall in forms.css is recorded, not fixed.
*/
.disclosure {
  margin: 1.25rem 0 0;
  padding-top: 0.75rem;
  border-top: solid 1px #f0f0f0;
  font-size: 0.85rem;
  color: #666666;
}

.confirm,
.composed {
  font-family: 'Gilroy', sans-serif;
}

.confirmTitle {
  margin: 0 0 0.5rem;
  font-family: 'Gilroy Bold', sans-serif;
  font-size: 1.1rem;
  color: #333333;
}

.summaryList {
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 0.25rem 0.75rem;
  margin: 1rem 0 0;
  font-size: 0.9rem;
}

.summaryList dt {
  color: #666666;
}

.summaryList dd {
  margin: 0;
  overflow-wrap: anywhere;
}

.message {
  margin: 0.75rem 0 0;
  padding: 0.75rem;
  border-radius: 4px;
  background-color: #f0f0f0;
  font-family: 'Courier New', monospace;
  font-size: 0.8rem;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  max-height: 14rem;
  overflow-y: auto;
}

.copyStatus {
  margin: 0.75rem 0 0;
  font-size: 0.85rem;
  color: #2f6f3e;
}
</style>
